import { Intervention } from '@/app/components/action-plan/types';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import SourceQuoteTag from '@/components/source-quote/source-quote';
import RiskBadge from '@/components/badges/risk-badge/risk-badge';
import { DownArrow } from '@/components/icons/down-arrow';
import { formatRiskDecimal, getCancerRiskSuffix, getRoute, normalizeCancerTypeName } from '@/utils';
import { useModalInterventionDetailsController } from './modal-intervention-details.controller';
import { useNavigate } from 'react-router-dom';
import { ROUTE_ACTION_PLAN, ROUTE_BUILD_ACTION_PLAN } from '@/constants/routes';
import { CloseButton } from '@/components/icons/close-button';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';

type ModalInterventionDetailsProps = {
  intervention: Intervention;
  isOpen: boolean;
  onClose: () => void;
  navToActionPlanHome: boolean;
};

export default function ModalInterventionDetails({ isOpen, onClose, intervention, navToActionPlanHome }: ModalInterventionDetailsProps) {
  const navigate = useNavigate();

  const {
    title,
    whyItMattersFullExplanation,
    riskImpactDescription,
    isNonQuantifiable,
    howToAccomplish,
    cancersAffected,
    riskImpactList,
    visibleSources,
    showLoadMoreButton,
    handleLoadMoreSources,
    sources,
  } = useModalInterventionDetailsController(intervention, isOpen);

  const handleActionPlanNavigation = (route: string) => {
    navigate(getRoute(route));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="dashboard-reset modal-backdrop" onClick={onClose}>
        {/* Modal Content */}
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-inner-content">
            {/* Header Section */}
            <div className="modal-header">
              <div className="modal-header-top">
                <div className="modal-title-container">
                  <h1 className="mobile-h2-primary m-0">
                    {title}
                  </h1>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-button"
                >
                  <CloseButton />
                </button>
              </div>
              {navToActionPlanHome ?
                <button
                  onClick={() => handleActionPlanNavigation(ROUTE_ACTION_PLAN)}
                  className="modal-action-button"
                >
                  View Action Plan
                </button>
                :
                <button
                  onClick={() => handleActionPlanNavigation(ROUTE_BUILD_ACTION_PLAN)}
                  className="modal-action-button"
                >
                  Build Action Plan
                </button>
              }
            </div>

            {/* Divider */}
            <div className="modal-divider" />

            {/* Why it matters section */}
            <div className="modal-section">
              <div className="modal-section-header">
                <div className="modal-section-icon-container">
                  <IconCatch icon="why-it-matters" size="s" transparentBackground />
                </div>
                <h2 className="modal-section-title">
                  Why it matters?
                </h2>
              </div>
              <div className="modal-section-content">
                <div className="modal-section-text">
                  <MarkdownTextTailwind>{whyItMattersFullExplanation}</MarkdownTextTailwind>
                </div>
              </div>
            </div>

            {/* Risk Impact section */}
            <div className="modal-section">
              <div className="modal-section-header">
                <div className="modal-section-icon-container">
                  <IconCatch icon="risk-impact" size="s" transparentBackground />
                </div>
                <h2 className="modal-section-title">
                  Risk Impact
                </h2>
              </div>
              <div className="modal-section-content modal-risk-impact-content">
                <div className="modal-section-text">
                  <MarkdownTextTailwind>{riskImpactDescription}</MarkdownTextTailwind>
                </div>
                {!isNonQuantifiable && (
                  <>
                    {riskImpactList.map(({ impact, cancerType }, index) => (
                      <div key={index} className="modal-risk-item">
                        <RiskBadge
                          value={parseFloat(formatRiskDecimal(impact))}
                          variant="success"
                          badgeType="change"
                          icon={<DownArrow />}
                          showPercent={true}
                        />
                        <div className="modal-risk-text">
                          {cancersAffected.length > 0
                            ? `Reduce your ${normalizeCancerTypeName(cancerType)}${getCancerRiskSuffix(cancerType)} risk`
                            : 'Reduce your overall cancer risk'}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* How to do it section */}
            <div className="modal-section">
              <div className="modal-section-header">
                <div className="modal-section-icon-container">
                  <IconCatch icon="how-to-accomplish" size="s" transparentBackground />
                </div>
                <h2 className="modal-section-title">
                  How to do it
                </h2>
              </div>
              <div className="modal-section-content">
                <div className="modal-section-text">
                  <MarkdownTextTailwind>{howToAccomplish}</MarkdownTextTailwind>
                </div>
              </div>
            </div>
            {/* The Science Section */}
            {sources && sources.length ? (
              <>
                <div className="modal-divider" />
                <div className="modal-science-section">
                  <h3 className="modal-science-title">
                    The science
                  </h3>
                  <SourceQuoteTag
                    sources={sources.slice(0, visibleSources)}
                    showLoadMoreButton={showLoadMoreButton}
                    loadMore={handleLoadMoreSources}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
